import React, { useMemo } from "react";
import StatusBadge from "../../ui/StatusBadge";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import { colors, spacing, fontSizes } from "../../ui/tokens";
import ControlEffectivenessBadge from "./ControlEffectivenessBadge";
import { useControlEffectivenessConfig } from "../../hooks/useControlEffectivenessConfig";
import {
  buildControlEffectivenessOptions,
  normalizeControlEffectivenessKey,
} from "../../utils/controlEffectiveness";

const sectionStyle = {
  border: "1px solid rgba(107, 114, 128, 0.2)",
  borderRadius: spacing.md,
  padding: spacing.lg,
  background: colors.background,
  display: "grid",
  gap: spacing.md,
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: spacing.md,
};

const labelStyle = {
  fontSize: fontSizes.sm,
  color: colors.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontWeight: 600,
};

const valueStyle = {
  fontSize: fontSizes.md,
  fontWeight: 600,
  color: colors.text,
};

const helperTextStyle = {
  fontSize: fontSizes.sm,
  color: colors.secondary,
};

const fieldErrorStyle = {
  fontSize: fontSizes.sm,
  color: colors.danger,
};

const resolveValue = (data, keys) => {
  if (!data) return undefined;
  for (const key of keys) {
    const value = data[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const formatDate = (value, fallback) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString();
};

export default function GovernanceSection({
  data,
  isEditing = false,
  isAuditView = false,
  status,
  canEdit = true,
  onChange,
  errors = {},
}) {
  const { items: effectivenessConfig } = useControlEffectivenessConfig();
  const values = useMemo(
    () => ({
      reviewCadence: resolveValue(data, ["review_cadence", "reviewCadence", "reviewFrequency", "review_frequency"]),
      lastReviewDate: resolveValue(data, ["last_review_date", "lastReviewDate", "last_reviewed_at"]),
      lastReviewedBy: resolveValue(data, ["last_reviewed_by", "lastReviewedBy"]),
      reviewOutcome: resolveValue(data, ["review_outcome", "reviewOutcome"]),
      effectiveness: resolveValue(data, ["effectiveness_rating", "effectiveness", "control_effectiveness"]),
      evidenceExpectation: resolveValue(data, ["evidence_expectation", "evidenceExpectation"]),
    }),
    [data]
  );

  const statusLabel = values.reviewCadence ? "Governance present" : "Not yet governed";
  const normalizedStatus = String(status || "").trim().toLowerCase();
  const isDraft = normalizedStatus === "draft";
  const isArchived = normalizedStatus === "archived";
  const isReadOnly = isAuditView || !canEdit || isArchived;
  const editEnabled = isEditing && !isReadOnly && typeof onChange === "function";
  const disableReviewFields = isDraft || isReadOnly;
  const disableCadenceFields = isReadOnly;
  const disableEvidenceExpectation = isReadOnly;
  const cadenceOptions = useMemo(() => {
    const base = [
      { value: "", label: "Not set" },
      { value: "monthly", label: "Monthly" },
      { value: "quarterly", label: "Quarterly" },
      { value: "semiannual", label: "Semiannual" },
      { value: "annual", label: "Annual" },
    ];
    const current = values.reviewCadence;
    if (current && !base.some((opt) => opt.value === current)) {
      base.push({ value: current, label: `Custom (${current})` });
    }
    return base;
  }, [values.reviewCadence]);

  const reviewOutcomeOptions = useMemo(() => {
    const base = [
      { value: "", label: "Not assessed" },
      { value: "reconfirmed", label: "Reconfirmed" },
      { value: "updated", label: "Updated" },
      { value: "failed", label: "Failed" },
    ];
    const current = values.reviewOutcome;
    if (current && !base.some((opt) => opt.value === current)) {
      base.push({ value: current, label: `Custom (${current})` });
    }
    return base;
  }, [values.reviewOutcome]);

  const effectivenessOptions = useMemo(() => {
    const base = buildControlEffectivenessOptions(effectivenessConfig);
    const current = normalizeControlEffectivenessKey(values.effectiveness, { blankKey: "" }) || values.effectiveness;
    if (current && !base.some((opt) => opt.value === current)) {
      base.push({ value: current, label: String(current).replace(/_/g, " ") });
    }
    return base;
  }, [effectivenessConfig, values.effectiveness]);

  const selectedEffectivenessValue =
    normalizeControlEffectivenessKey(values.effectiveness, { blankKey: "" }) || values.effectiveness || "";

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
          <h3 style={{ margin: 0 }}>Governance</h3>
          <StatusBadge label={statusLabel} fallbackLabel={statusLabel} />
        </div>
      </div>

      <div style={fieldGridStyle}>
        <div>
          <div style={labelStyle}>Review Frequency</div>
          {editEnabled ? (
            <>
              <Select
                ariaLabel="Review Frequency"
                value={values.reviewCadence || ""}
                onChange={(e) => onChange?.("review_cadence", e.target.value)}
                disabled={disableCadenceFields}
                error={errors.review_cadence}
                options={cadenceOptions}
              />
              {errors.review_cadence && <div style={fieldErrorStyle}>{errors.review_cadence}</div>}
            </>
          ) : (
            <div style={valueStyle}>{values.reviewCadence || "Not set"}</div>
          )}
        </div>
        <div>
          <div style={labelStyle}>Last Review Date</div>
          {editEnabled ? (
            <>
              <Input
                type="date"
                ariaLabel="Last Review Date"
                value={values.lastReviewDate ? new Date(values.lastReviewDate).toISOString().slice(0, 10) : ""}
                onChange={(e) => onChange?.("last_review_date", e.target.value)}
                disabled={disableReviewFields}
                error={errors.last_review_date}
              />
              {errors.last_review_date && <div style={fieldErrorStyle}>{errors.last_review_date}</div>}
            </>
          ) : (
            <div style={valueStyle}>{formatDate(values.lastReviewDate, "Not yet reviewed")}</div>
          )}
        </div>
        <div>
          <div style={labelStyle}>Last Reviewed By</div>
          {editEnabled ? (
            <>
              <Input
                ariaLabel="Last Reviewed By"
                value={values.lastReviewedBy || ""}
                onChange={(e) => onChange?.("last_reviewed_by", e.target.value)}
                disabled={disableReviewFields}
                error={errors.last_reviewed_by}
                placeholder="Reviewer name"
              />
              {errors.last_reviewed_by && <div style={fieldErrorStyle}>{errors.last_reviewed_by}</div>}
            </>
          ) : (
            <div style={valueStyle}>{values.lastReviewedBy || "Not set"}</div>
          )}
        </div>
        <div>
          <div style={labelStyle}>Review Outcome</div>
          {editEnabled ? (
            <>
              <Select
                ariaLabel="Review Outcome"
                value={values.reviewOutcome || ""}
                onChange={(e) => onChange?.("review_outcome", e.target.value)}
                disabled={disableReviewFields}
                error={errors.review_outcome}
                options={reviewOutcomeOptions}
              />
              {errors.review_outcome && <div style={fieldErrorStyle}>{errors.review_outcome}</div>}
            </>
          ) : (
            <div style={valueStyle}>{values.reviewOutcome || "Not assessed"}</div>
          )}
        </div>
        <div>
          <div style={labelStyle}>Effectiveness Rating</div>
          {editEnabled ? (
            <>
              <Select
                ariaLabel="Effectiveness Rating"
                value={selectedEffectivenessValue}
                onChange={(e) => onChange?.("effectiveness_rating", e.target.value)}
                disabled={disableReviewFields}
                error={errors.effectiveness_rating}
                options={effectivenessOptions}
              />
              <div style={{ marginTop: spacing.xs }}>
                <ControlEffectivenessBadge value={selectedEffectivenessValue} config={effectivenessConfig} />
              </div>
              {errors.effectiveness_rating && <div style={fieldErrorStyle}>{errors.effectiveness_rating}</div>}
            </>
          ) : (
            <div style={valueStyle}>
              <ControlEffectivenessBadge value={values.effectiveness} config={effectivenessConfig} />
            </div>
          )}
        </div>
        <div>
          <div style={labelStyle}>Evidence Expectation</div>
          {editEnabled ? (
            <>
              <Textarea
                ariaLabel="Evidence Expectation"
                value={values.evidenceExpectation || ""}
                onChange={(e) => onChange?.("evidence_expectation", e.target.value)}
                disabled={disableEvidenceExpectation}
                error={errors.evidence_expectation}
                rows={3}
              />
              {errors.evidence_expectation && <div style={fieldErrorStyle}>{errors.evidence_expectation}</div>}
            </>
          ) : (
            <div style={valueStyle}>{values.evidenceExpectation || "Not set"}</div>
          )}
        </div>
      </div>

      {isAuditView && (
        <div style={helperTextStyle}>
          Audit View enabled. Governance fields are read-only.
        </div>
      )}
      {!isAuditView && isArchived && (
        <div style={helperTextStyle}>Archived controls are read-only.</div>
      )}
      {!isAuditView && !isArchived && !canEdit && (
        <div style={helperTextStyle}>You do not have permission to edit governance.</div>
      )}
      {!isAuditView && !isArchived && canEdit && isDraft && (
        <div style={helperTextStyle}>Available once control is Active.</div>
      )}
    </section>
  );
}
